import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  Position,
  useInternalNode,
  useStore,
  ReactFlowState,
} from '@xyflow/react';
import { memo, useMemo } from 'react';

type XYPosition = {
  x: number;
  y: number;
};

/**
 * Get the absolute position of a node (accounting for parent transforms).
 * In @xyflow/react v12+, internals provide positionAbsolute.
 */
const getNodePosition = (node: any): XYPosition => {
  // v12 uses node.internals.positionAbsolute
  if (node?.internals?.positionAbsolute) {
    return node.internals.positionAbsolute;
  }
  // Fallback to node.positionAbsolute (v11 compat) or node.position
  return node?.positionAbsolute ?? node?.position ?? { x: 0, y: 0 };
};

/**
 * Get the measured dimensions of a node.
 */
const getNodeDimensions = (node: any): { width: number; height: number } => {
  const width = node?.measured?.width ?? node?.width ?? 200;
  const height = node?.measured?.height ?? node?.height ?? 100;
  return { width, height };
};

/**
 * Get center coordinates of a node.
 */
const getNodeCenter = (node: any): XYPosition => {
  const pos = getNodePosition(node);
  const { width, height } = getNodeDimensions(node);
  return {
    x: pos.x + width / 2,
    y: pos.y + height / 2,
  };
};

/**
 * Calculate the intersection point where a line from `from` to `to`
 * crosses the border of a rectangular node.
 * This ensures edges connect exactly at the node boundary.
 */
const getNodeBorderIntersection = (
  nodeCenter: XYPosition,
  otherCenter: XYPosition,
  node: any
): { point: XYPosition; position: Position } => {
  const pos = getNodePosition(node);
  const { width, height } = getNodeDimensions(node);

  const left = pos.x;
  const right = pos.x + width;
  const top = pos.y;
  const bottom = pos.y + height;

  // Direction from node center toward the other node center
  const dx = otherCenter.x - nodeCenter.x;
  const dy = otherCenter.y - nodeCenter.y;

  // Handle zero direction (nodes are overlapping)
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
    return {
      point: { x: nodeCenter.x, y: top },
      position: Position.Top,
    };
  }

  // Calculate intersection with all 4 sides using parametric ray from center
  const candidates: Array<{ t: number; point: XYPosition; position: Position }> = [];

  // Left side
  if (dx !== 0) {
    const tLeft = (left - nodeCenter.x) / dx;
    if (tLeft > 0) {
      const y = nodeCenter.y + tLeft * dy;
      if (y >= top && y <= bottom) {
        candidates.push({ t: tLeft, point: { x: left, y }, position: Position.Left });
      }
    }

    // Right side
    const tRight = (right - nodeCenter.x) / dx;
    if (tRight > 0) {
      const y = nodeCenter.y + tRight * dy;
      if (y >= top && y <= bottom) {
        candidates.push({ t: tRight, point: { x: right, y }, position: Position.Right });
      }
    }
  }

  if (dy !== 0) {
    // Top side
    const tTop = (top - nodeCenter.y) / dy;
    if (tTop > 0) {
      const x = nodeCenter.x + tTop * dx;
      if (x >= left && x <= right) {
        candidates.push({ t: tTop, point: { x, y: top }, position: Position.Top });
      }
    }

    // Bottom side
    const tBottom = (bottom - nodeCenter.y) / dy;
    if (tBottom > 0) {
      const x = nodeCenter.x + tBottom * dx;
      if (x >= left && x <= right) {
        candidates.push({ t: tBottom, point: { x, y: bottom }, position: Position.Bottom });
      }
    }
  }

  // Pick the closest intersection (smallest positive t)
  if (candidates.length === 0) {
    // Fallback: closest edge center
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (absDx > absDy) {
      return dx > 0
        ? { point: { x: right, y: nodeCenter.y }, position: Position.Right }
        : { point: { x: left, y: nodeCenter.y }, position: Position.Left };
    } else {
      return dy > 0
        ? { point: { x: nodeCenter.x, y: bottom }, position: Position.Bottom }
        : { point: { x: nodeCenter.x, y: top }, position: Position.Top };
    }
  }

  candidates.sort((a, b) => a.t - b.t);
  return { point: candidates[0].point, position: candidates[0].position };
};

/**
 * Build a quadratic Bézier path between two points with a perpendicular
 * offset on the midpoint. `offset` is the curvature in pixels — positive
 * curves to one side, negative to the other, 0 yields a straight line.
 *
 * Returns the SVG `d` attribute and the (x, y) where the label should sit
 * (which is the actual midpoint of the curve, not just the midpoint of
 * source/target — important so labels track the curve nicely).
 */
const buildCurvedPath = (
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  offset: number,
): { path: string; labelX: number; labelY: number } => {
  if (offset === 0) {
    return {
      path: `M ${sx},${sy} L ${tx},${ty}`,
      labelX: (sx + tx) / 2,
      labelY: (sy + ty) / 2,
    };
  }

  // Midpoint between the two anchor points
  const mx = (sx + tx) / 2;
  const my = (sy + ty) / 2;

  // Perpendicular unit vector to the source→target direction
  const dx = tx - sx;
  const dy = ty - sy;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;

  // Control point pushed perpendicularly from the midpoint
  const cx = mx + nx * offset;
  const cy = my + ny * offset;

  // Label sits at the actual curve midpoint t=0.5:
  // B(0.5) = 0.25*P0 + 0.5*P1 + 0.25*P2  for a quadratic Bézier
  const labelX = 0.25 * sx + 0.5 * cx + 0.25 * tx;
  const labelY = 0.25 * sy + 0.5 * cy + 0.25 * ty;

  return {
    path: `M ${sx},${sy} Q ${cx},${cy} ${tx},${ty}`,
    labelX,
    labelY,
  };
};

// Selector: pull the (relevant) edge list out of the React Flow store so
// every FloatingEdge can compute its place among any parallel siblings.
// We project to the minimum we need — id/source/target — to keep the
// referential equality check tight (the selector re-runs on every store
// update; cheaper projections = fewer re-renders).
type EdgePair = { id: string; source: string; target: string };

const selectEdgePairs = (s: ReactFlowState): EdgePair[] =>
  s.edges.map((e) => ({ id: e.id, source: e.source, target: e.target }));

/**
 * Shallow-compare two EdgePair arrays so `useStore` only triggers a
 * re-render when the edge topology actually changes (add/remove edge),
 * NOT on every unrelated store update (e.g. node drag). Without this,
 * every FloatingEdge re-renders on every frame during pan/drag.
 */
const edgePairsEqual = (a: EdgePair[], b: EdgePair[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id || a[i].source !== b[i].source || a[i].target !== b[i].target) {
      return false;
    }
  }
  return true;
};

/**
 * FloatingEdge - A custom edge that connects to the nearest border point
 * of source and target nodes, creating ontology-style connections where
 * arrows always touch the closest edge of each node.
 *
 * Multi-edge handling
 * -------------------
 * If two or more edges connect the same pair of nodes (in either
 * direction), they would otherwise stack on top of each other and look
 * like a single relationship. To fix that we:
 *   1. Collect all edges between this same unordered pair from the store.
 *   2. Sort them deterministically by id so each edge gets a stable
 *      "slot" index (no jumping when other unrelated edges are added).
 *   3. Compute a perpendicular curve offset based on the slot index.
 *      Edges that go in the OPPOSITE direction relative to the first
 *      edge in the group get their offset sign flipped, so a
 *      "depends on" / "is depended by" pair fans out symmetrically.
 *   4. Render the path as a quadratic Bézier (or a straight line when
 *      offset == 0).
 */
const FloatingEdgeInner = ({
  id,
  source,
  target,
  markerEnd,
  style,
  label,
  selected,
  data,
}: EdgeProps) => {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const allEdgePairs = useStore(selectEdgePairs, edgePairsEqual);

  // Find all edges that connect this same pair of nodes (regardless of
  // direction) and where THIS edge sits among them. The result is stable
  // across re-renders because we sort by id.
  const { offset } = useMemo(() => {
    const siblings = allEdgePairs
      .filter(
        (e) =>
          (e.source === source && e.target === target) ||
          (e.source === target && e.target === source),
      )
      .sort((a, b) => a.id.localeCompare(b.id));

    const count = siblings.length;
    if (count <= 1) return { offset: 0 };

    const myIndex = siblings.findIndex((e) => e.id === id);
    if (myIndex === -1) return { offset: 0 };

    // Direction of the FIRST sibling defines the "canonical" direction
    // for this group. Any sibling that flows the opposite way gets a
    // negative offset so the two arrows fan apart visually.
    const canonical = siblings[0];
    const myDirectionMatches = canonical.source === source;

    // Distribute slots symmetrically around 0:
    //   2 edges → slots [-1, +1] (× spacing)
    //   3 edges → slots [-2,  0, +2]
    //   4 edges → slots [-3, -1, +1, +3]
    // This keeps the bundle visually centered on the straight line.
    const SPACING = 28; // px of perpendicular curvature per slot
    const slot = myIndex - (count - 1) / 2;
    const baseOffset = slot * SPACING * 2;
    const signedOffset = myDirectionMatches ? baseOffset : -baseOffset;

    return { offset: signedOffset };
  }, [allEdgePairs, id, source, target]);

  if (!sourceNode || !targetNode) {
    return null;
  }

  const sourceCenter = getNodeCenter(sourceNode);
  const targetCenter = getNodeCenter(targetNode);

  // Calculate where edge exits the source node border
  const sourceIntersection = getNodeBorderIntersection(sourceCenter, targetCenter, sourceNode);
  // Calculate where edge enters the target node border
  const targetIntersection = getNodeBorderIntersection(targetCenter, sourceCenter, targetNode);

  const { path: edgePath, labelX, labelY } = buildCurvedPath(
    sourceIntersection.point.x,
    sourceIntersection.point.y,
    targetIntersection.point.x,
    targetIntersection.point.y,
    offset,
  );

  const edgeData = data as {
    hint?: unknown;
    relationshipType?: unknown;
    label?: unknown;
    unsaved?: unknown;
    provenance?: unknown;
    updatedByName?: unknown;
  } | undefined;

  const isUnsaved = edgeData?.unsaved === true;
  const provenance = typeof edgeData?.provenance === 'string' ? edgeData.provenance : 'ai';
  // Show the small badge on the canvas for corrected/user-authored edges so the
  // edited state is visible after a reload (same concept as the ConceptNode badge).
  const showEditedBadge = provenance === 'corrected' || provenance === 'user';
  const editedBadgeLabel = provenance === 'user' ? 'You' : 'Edited';
  const hint = typeof edgeData?.hint === 'string' ? edgeData.hint : undefined;

  // Show the short relationship-type token on the edge (e.g. "uses", "is-a").
  // Fall back to the top-level `label` prop for legacy edges that have no data.
  const displayLabel =
    typeof edgeData?.relationshipType === 'string' && edgeData.relationshipType
      ? edgeData.relationshipType
      : label;

  // Use data.label (human-readable) as the tooltip when it differs from the display label.
  const tooltipLabel =
    typeof edgeData?.label === 'string' && edgeData.label !== displayLabel
      ? edgeData.label
      : hint;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: selected
            ? 'var(--cds-focus)'
            : isUnsaved
            ? 'var(--cds-support-warning, #f1c21b)'
            : style?.stroke,
          strokeWidth: selected ? 3 : (style?.strokeWidth ?? 2),
          strokeDasharray: isUnsaved && !selected ? '6 3' : undefined,
        }}
      />
      {displayLabel ? (
        <EdgeLabelRenderer>
          {/*
            `data.hint` explains an edge the source does NOT assert (a deduced instance relation,
            EdgeKind 'instance_relation'). Exposed on hover AND to assistive technology: a dotted
            stroke alone says "different", never "deduced".
          */}
          {/*
            Outer div: absolutely positioned at (labelX, labelY) — exactly
            what labelStyle was doing. Set position:relative so the inner
            badge can be positioned relative to this container.
          */}
          {/*
            Outer container: centred on (labelX, labelY), flex column so the
            provenance badge sits below the label pill without overlapping it.
          */}
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '3px',
            }}
          >
            {/* Label pill */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 11,
                fontWeight: 500,
                lineHeight: 1,
                color: selected ? 'var(--cds-focus)' : 'var(--cds-text-primary)',
                background: 'var(--cds-layer-01)',
                padding: '3px 8px',
                borderRadius: 9999,
                border: selected
                  ? '1px solid var(--cds-focus)'
                  : '1px solid var(--cds-border-subtle-01)',
                whiteSpace: 'nowrap',
                boxShadow: selected
                  ? '0 0 0 2px var(--cds-focus-inset, rgba(15, 98, 254, 0.2))'
                  : '0 1px 3px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              title={tooltipLabel}
              aria-label={tooltipLabel ? `${String(displayLabel)}. ${tooltipLabel}` : undefined}
              data-edge-hint={hint ? 'true' : undefined}
            >
              {String(displayLabel)}
            </div>
            {/*
              Provenance badge — sits below the pill so it never masks the label.
              Mirrors the ConceptNode "Edited"/"You" pip; hidden while unsaved
              since the dashed warning stroke already signals that state.
            */}
            {showEditedBadge && !isUnsaved && (
              <span
                data-testid="edge-provenance-badge"
                title={provenance === 'user' ? 'Added by you' : 'AI-generated, then corrected'}
                style={{
                  backgroundColor:
                    provenance === 'user'
                      ? 'var(--cds-interactive-01)'
                      : 'var(--cds-support-warning, #f1c21b)',
                  color:
                    provenance === 'user'
                      ? 'white'
                      : 'var(--cds-text-on-color-disabled, #161616)',
                  fontSize: '0.5rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  padding: '0.15rem 0.4rem',
                  borderRadius: '0.75rem',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {editedBadgeLabel}
              </span>
            )}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
};

/**
 * Memoized FloatingEdge. React Flow passes new props on every render cycle
 * even if nothing relevant changed. memo() + the equality fn on useStore
 * ensures we only re-render when the edge's own data or connected node
 * positions actually change.
 */
export const FloatingEdge = memo(FloatingEdgeInner);

export default FloatingEdge;
